import { memo } from 'react';
import type { ObjectShape, ObjectShapeLayer, ObjectShapeStyle } from 'shared-types';
import { T, useT } from '../../../translation/src';
import { addModifierChoice, groupForModifiers, roundCoord, toPath, updateShape, type ModifierChoice } from '../document/specOps';
import { useEditorState, useObjectEditor } from '../EditorContext';
import { FieldRow, NumberField, PanelButton, PanelSection, Segmented, TextField, ToggleRow } from '../panels/fields';
import { useShapeLabel } from '../panels/LayersPanel';
import { AddModifierMenu } from './ModifierStack';
import { PointPins } from './PointPins';

const DEG = 180 / Math.PI;

/** Lines and rectangles become a bezier path whose points can then be rounded. */
const ConvertToCurve: React.FC<{ onConvert: () => void }> = ({ onConvert }) => (
  <FieldRow label={null} hint={<T _str="then round its points with the edit points tool (N)" swc />}>
    <PanelButton onClick={onConvert}>
      <T _str="convert to curve" swc />
    </PanelButton>
  </FieldRow>
);

/** Exact size of one shape (where it sits is set by dragging it on the canvas). */
const Geometry: React.FC<{ shape: ObjectShape; update: (next: ObjectShape) => void }> = ({ shape, update }) => {
  switch (shape.kind) {
    case 'rect':
      return (
        <>
          <FieldRow label={<T _str="size" swc />}>
            <span className="grid grid-cols-2 gap-1">
              <NumberField value={shape.max[0] - shape.min[0]} unit="m" min={0.001} onCommit={(w) => update({ ...shape, max: [roundCoord(shape.min[0] + w), shape.max[1]] })} />
              <NumberField value={shape.max[1] - shape.min[1]} unit="m" min={0.001} onCommit={(d) => update({ ...shape, max: [shape.max[0], roundCoord(shape.min[1] + d)] })} />
            </span>
          </FieldRow>
          <ConvertToCurve onConvert={() => update(toPath(shape))} />
        </>
      );
    case 'circle':
      return (
        <FieldRow label={<T _str="radius" swc />}>
          <NumberField value={shape.radius} unit="m" min={0.001} onCommit={(radius) => update({ ...shape, radius })} />
        </FieldRow>
      );
    case 'arc':
      return (
        <>
          <FieldRow label={<T _str="radius" swc />}>
            <NumberField value={shape.radius} unit="m" min={0.001} onCommit={(radius) => update({ ...shape, radius })} />
          </FieldRow>
          <FieldRow label={<T _str="angles" swc />}>
            <span className="grid grid-cols-2 gap-1">
              <NumberField value={shape.startAngle * DEG} unit="°" decimals={1} onCommit={(a) => update({ ...shape, startAngle: a / DEG })} />
              <NumberField value={shape.endAngle * DEG} unit="°" decimals={1} onCommit={(a) => update({ ...shape, endAngle: a / DEG })} />
            </span>
          </FieldRow>
        </>
      );
    case 'polyline':
      return (
        <>
          <FieldRow label={<T _str="points" swc />}>
            <span className="text-sm">{shape.points.length}</span>
          </FieldRow>
          <ToggleRow label={<T _str="closed outline" swc />} checked={shape.closed} onChange={(closed) => update({ ...shape, closed: closed && shape.points.length > 2 })} />
          <ConvertToCurve onConvert={() => update(toPath(shape))} />
        </>
      );
    case 'path':
      return (
        <>
          <FieldRow label={<T _str="points" swc />} hint={<T _str="with the edit points tool (N): double-click a point to make it sharp or smooth; Alt-drag a handle to break it" swc />}>
            <span className="text-sm">{shape.nodes.length}</span>
          </FieldRow>
          <ToggleRow label={<T _str="closed outline" swc />} checked={shape.closed} onChange={(closed) => update({ ...shape, closed: closed && shape.nodes.length > 2 })} />
        </>
      );
  }
};

/** One or more selected shapes: name, geometry (single), line style, when it shows, and actions. */
const ShapeInspector_: React.FC<{ ids: readonly string[] }> = ({ ids }) => {
  const { store, ui } = useObjectEditor();
  const { spec } = useEditorState();
  const shapeLabel = useShapeLabel();
  const groupName = useT('group', { swc: true });
  const shapes = spec.shapes.filter((s) => ids.includes(s.id));
  if (shapes.length === 0) return null;
  const single = shapes.length === 1 ? shapes[0] : null;
  const style: ObjectShapeStyle = shapes.every((s) => s.style === 'dashed') ? 'dashed' : 'solid';
  const layer: ObjectShapeLayer = shapes.every((s) => s.layer === 'selection') ? 'selection' : 'glyph';

  const updateAll = (patch: (s: ObjectShape) => ObjectShape) => store.apply((sp) => ids.reduce((acc, id) => updateShape(acc, id, patch), sp));

  /** Modifiers live on groups: the selection becomes one (unless it already is exactly a group), gets the modifier and is selected. */
  const addModifierToSelection = (choice: ModifierChoice) => {
    const grouped = groupForModifiers(store.getState().spec, ids, `${groupName} ${spec.groups.length + 1}`);
    if (!grouped) return;
    store.apply(() => addModifierChoice(grouped.spec, grouped.groupId, choice), { kind: 'group', id: grouped.groupId });
  };

  return (
    <>
      <PanelSection title={single ? shapeLabel(single, spec.shapes.indexOf(single)) : <T _str="{count} shapes" args={{ count: shapes.length }} />}>
        {single && (
          <>
            <FieldRow label={<T _str="name" swc />}>
              <TextField value={single.name ?? ''} maxLength={80} onCommit={(name) => updateAll((s) => ({ ...s, name: name.trim() || undefined }))} />
            </FieldRow>
            <Geometry shape={single} update={(next) => store.apply((sp) => updateShape(sp, single.id, () => next))} />
          </>
        )}
        <FieldRow label={<T _str="line" swc />}>
          <Segmented
            value={style}
            options={[
              { value: 'solid', label: <T _str="solid" swc /> },
              { value: 'dashed', label: <T _str="dashed" swc /> },
            ]}
            onChange={(next) => updateAll((s) => ({ ...s, style: next }))}
          />
        </FieldRow>
        <FieldRow label={<T _str="shown" swc />} hint={layer === 'selection' ? <T _str="drawn only while the placed object is selected, e.g. a center line" swc /> : undefined}>
          <Segmented
            value={layer}
            options={[
              { value: 'glyph', label: <T _str="always" swc /> },
              { value: 'selection', label: <T _str="selected" swc /> },
            ]}
            onChange={(next) => updateAll((s) => ({ ...s, layer: next }))}
          />
        </FieldRow>
      </PanelSection>

      {single && ui.tool === 'node' && ui.node?.shapeId === single.id && <PointPins spec={spec} shape={single} index={ui.node.index} />}

      {single?.expr && (
        <p className="px-4 py-2 text-xs text-gray-500 dark:text-gray-400">
          <T _str="its points follow rules (see the Rules list); pick a point with the Edit points tool (N) to change one" swc />
        </p>
      )}

      {/* Modifiers live on groups: adding one groups the selection (unless it already is a group) and shows its stack. */}
      <PanelSection title={<T _str="modifiers" />} actions={<AddModifierMenu hasRepeat={false} onPick={addModifierToSelection} />} />
    </>
  );
};

export const ShapeInspector = memo(ShapeInspector_);
